import { SiteShell, ImagePlaceholder } from "./SiteShell";
import { PageHero, Section, FeatureGrid, CTA, Breadcrumbs, FAQGrid, Prose, type HeroVariant } from "./Sections";
import { Link } from "@tanstack/react-router";
import { LOCATIONS } from "./locations";

export type LocationData = {
  slug: string;
  city: string;
  area?: string;
  postcode?: string;
  introLead?: string;
  vibe?: string;             // 1-2 sætninger om områdets karakter
  housing?: string;          // typisk boligmasse i området
  neighborhoods?: string[];  // delområder / kvarterer
  landmarks?: string[];      // lokale landmarks
  typicalJobs?: string[];    // mest efterspurgte ydelser her
  responseMinutes?: number;  // typisk udrykning i minutter
  heroVariant?: HeroVariant;
  accentColor?: string;      // hex/oklch
};

const HERO_ROTATION: HeroVariant[] = ["split", "centered", "compact", "stats"];

export function LocationPage({ data }: { data: LocationData }) {
  const { city, slug, postcode } = data;
  const variant = data.heroVariant ?? HERO_ROTATION[(slug.length) % HERO_ROTATION.length];
  const responseMinutes = data.responseMinutes ?? 90;

  const services = data.typicalJobs && data.typicalJobs.length >= 4
    ? data.typicalJobs.map((t) => ({ title: t, body: localJobBody(t, city) }))
    : defaultServices(city);

  return (
    <SiteShell>
      <Breadcrumbs
        items={[
          { label: "Forside", to: "/" },
          { label: "Områder", to: "/sitemap" },
          { label: `Elektriker ${city}` },
        ]}
      />
      <PageHero
        variant={variant}
        accentColor={data.accentColor}
        eyebrow={`Lokal elektriker · ${city}${postcode ? ` · ${postcode}` : ""}`}
        title={`Elektriker i ${city}`}
        intro={
          data.introLead ??
          `Autoriseret elektriker med base i Storkøbenhavn og daglig kørsel i ${city}. Vi løser akutte fejl, planlagte el-installationer og større renoveringer for både private og erhverv, med fast pris, dokumentation og garanti.`
        }
        imageLabel={`Elektriker i ${city}`}
        stats={[
          { value: `${responseMinutes} min`, label: `Typisk udrykning i ${city}` },
          { value: "Garanti", label: "På arbejdet" },
          { value: "AAA", label: "Kreditværdighed" },
          { value: "24/7", label: "Døgnvagt" },
        ]}
      />

      {/* Lokal intro / vibe */}
      <Section className="!pt-12">
        <Prose>
          <h2>Din lokale elektriker i {city}</h2>
          <p>
            {data.vibe ??
              `${city} er et område vi kender godt. Vi har dagligt biler i kvarteret og kan derfor være hos dig hurtigt, typisk inden for ${responseMinutes} minutter ved akutte opgaver i åbningstiden.`}{" "}
            Vi er en autoriseret elinstallatørvirksomhed (CVR 35209468) med erfarne svende, der både laver service­opgaver og store entrepriser. Når du ringer til os, kommer der ikke en sælger ud, der kommer en elektriker, der kan løse opgaven.
          </p>
          {data.housing && (
            <>
              <h3>Boliger og bygninger i {city}</h3>
              <p>{data.housing}</p>
              <p>
                Det stiller forskellige krav til el-installationen. I ældre ejendomme støder vi ofte på aldrende tavler, manglende HPFI og kabler ført uden hensyn til moderne belastning fra induktionskogeplader, varmepumper og ladestandere. I nyere byggeri handler det oftere om smart-home, KNX-styring og integration med solceller, batterier og elbilladning. Vi rådgiver dig ærligt, og vi sælger dig kun det, der reelt løser problemet.
              </p>
            </>
          )}
        </Prose>
      </Section>

      {/* Services */}
      <Section className="!pt-0">
        <div className="mb-10 max-w-2xl">
          <h2 className="text-3xl">Elektrikerydelser vi laver i {city}</h2>
          <p className="mt-3 text-muted-foreground">
            Vi er din lokale elektriker i {city}{postcode ? ` (${postcode})` : ""}. Herunder finder du de opgaver vi oftest løser i området, men listen er ikke udtømmende. Mangler du noget specifikt, så ring og spørg.
          </p>
        </div>
        <FeatureGrid items={services} />
      </Section>

      {/* Neighborhoods + landmarks */}
      {(data.neighborhoods?.length || data.landmarks?.length) && (
        <Section className="!pt-0">
          <div className="grid gap-10 md:grid-cols-2">
            {data.neighborhoods && data.neighborhoods.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-8">
                <h2 className="text-2xl">Kvarterer i {city} vi dækker</h2>
                <p className="mt-3 text-muted-foreground">
                  Vi kører elektrikeropgaver overalt i {city}, bl.a. i disse kvarterer og lommer:
                </p>
                <ul className="mt-5 grid grid-cols-2 gap-2 text-sm">
                  {data.neighborhoods.map((n) => (
                    <li key={n} className="rounded-md border border-border bg-background px-3 py-2 text-[var(--ink)]/85">
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.landmarks && data.landmarks.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-8">
                <h2 className="text-2xl">Vi kender området</h2>
                <p className="mt-3 text-muted-foreground">
                  Vi har tidligere arbejdet på eller omkring:
                </p>
                <ul className="mt-5 space-y-2 text-sm text-[var(--ink)]/85">
                  {data.landmarks.map((l) => (
                    <li key={l} className="flex gap-2">
                      <span aria-hidden className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      {l}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-sm text-muted-foreground">
                  Lokalkendskab gør en reel forskel, vi kender adgangsforhold, parkering, ejerforeningernes vedtægter og de byggetekniske særtræk i {city}.
                </p>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Why us */}
      <Section className="!py-10">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <ImagePlaceholder label={`Elektriker på arbejde i ${city}`} ratio="aspect-[5/4]" />
          <div>
            <h2 className="text-3xl">Hvorfor vælge os i {city}?</h2>
            <ul className="mt-5 space-y-3 text-[var(--ink)]/85">
              {[
                "Autoriseret elinstallatør med fuldt forsikret personale",
                `Hurtig udrykning i ${city}, ofte samme dag, typisk inden for ${responseMinutes} min ved akut`,
                "Faste, gennemskuelige priser og altid skriftligt tilbud før vi går i gang",
                "Garanti på vores el-installationer + TEKNIQ Garanti op til 150.000 kr.",
                "Erfarne medarbejdere der bor og kører i Storkøbenhavn, ikke underleverandører",
                "Vi rydder op efter os og forlader dit hjem som vi fandt det",
              ].map((b) => (
                <li key={b} className="flex gap-3">
                  <span aria-hidden className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l4 4 10-10" /></svg>
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/tilbud" className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-95">Få et tilbud</Link>
              <Link to="/priser-privat" className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">Se priser</Link>
            </div>
          </div>
        </div>
      </Section>

      {/* Process */}
      <Section className="!pt-0">
        <h2 className="text-3xl">Sådan forløber en opgave i {city}</h2>
        <ol className="mt-8 grid gap-6 md:grid-cols-4">
          {[
            { n: "1", t: "Du ringer eller skriver", b: `Beskriv kort opgaven. Vi vurderer om vi kan løse det over telefonen, om vi skal forbi til besigtigelse, eller om vi rykker ud akut i ${city}.` },
            { n: "2", t: "Pris og aftale", b: "Vi giver fast pris eller et veldokumenteret estimat. Du får alt skriftligt før vi går i gang." },
            { n: "3", t: "Vi udfører arbejdet", b: "Autoriseret elektriker udfører opgaven, oplyser om afvigelser undervejs og rydder op efter sig." },
            { n: "4", t: "Dokumentation", b: "Du får KLS-rapport, faktura og dokumentation digitalt, og garanti på arbejdet." },
          ].map((s) => (
            <li key={s.n} className="rounded-2xl border border-border bg-card p-6">
              <div className="mb-3 grid h-9 w-9 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{s.n}</div>
              <h3 className="font-semibold text-[var(--ink)]">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.b}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* FAQ */}
      <Section className="!pt-0">
        <h2 className="text-3xl">Ofte stillede spørgsmål, elektriker i {city}</h2>
        <div className="mt-8">
          <FAQGrid
            items={[
              { q: `Hvor hurtigt kan I være i ${city}?`, a: `Vi har biler i området hver dag og kan ofte være hos dig samme dag. Ved akut udrykning i åbningstiden er vi typisk fremme inden for ${responseMinutes} minutter i ${city}.` },
              { q: `Hvad koster en elektriker i ${city}?`, a: `Vi arbejder med faste timepriser og giver skriftligt tilbud med fast pris på planlagte opgaver. Se vores fulde prisliste på siden Priser, det er de samme priser i ${city} som i resten af Storkøbenhavn.` },
              { q: `Er I autoriserede?`, a: `Ja. Vi er en autoriseret elinstallatørvirksomhed (CVR 35209468), medlem af TEKNIQ Arbejdsgiverne og har AAA kreditværdighed. Du får garanti på alt vores arbejde.` },
              { q: `Laver I også opgaver for erhverv og foreninger i ${city}?`, a: `Ja. Vi servicerer private, ejer- og andelsforeninger, butikker, restauranter og virksomheder i ${city}. Vi kan også indgå serviceaftale med rabatterede timepriser.` },
              { q: `Kan jeg få fast pris på en større opgave?`, a: `Ja, på planlagte opgaver giver vi skriftligt tilbud med fast pris efter besigtigelse i ${city}. Du ved præcis hvad det koster, før vi går i gang.` },
              { q: `Kører I akut om aftenen og i weekenden?`, a: `Ja, vi har døgnvagt 24/7. Akut udrykning uden for åbningstid afregnes efter takst, se priser-siden.` },
            ]}
          />
        </div>
      </Section>

      <CTA title={`Brug for en elektriker i ${city}?`} body={`Ring eller send en uforpligtende forespørgsel. Vi er typisk fremme i ${city} inden for ${responseMinutes} minutter ved akutte opgaver.`} />

      {/* Internal linking — andre områder vi dækker */}
      <Section className="!pt-0">
        <div className="rounded-2xl border border-border bg-card p-8">
          <h2 className="text-2xl">Vi dækker også disse områder</h2>
          <p className="mt-3 text-muted-foreground">
            Bor du i nærheden af {city}? Vi er din lokale elektriker i hele Storkøbenhavn og Nordsjælland. Se også vores sider for:
          </p>
          <ul className="mt-5 grid grid-cols-2 gap-2 text-sm md:grid-cols-3 lg:grid-cols-4">
            {LOCATIONS
              .filter((l) => l.slug !== slug)
              .slice()
              .sort((a, b) => ((a.slug.charCodeAt(0) + slug.length) % 7) - ((b.slug.charCodeAt(0) + slug.length) % 7))
              .slice(0, 12)
              .map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/${l.slug}` as string}
                    className="block rounded-md border border-border bg-background px-3 py-2 text-[var(--ink)]/85 hover:border-primary hover:text-primary"
                  >
                    Elektriker {l.city}
                  </Link>
                </li>
              ))}
          </ul>
          <p className="mt-5 text-sm text-muted-foreground">
            <Link to="/byer" className="font-semibold text-primary hover:underline">Se alle {LOCATIONS.length} byer →</Link>
          </p>
        </div>
      </Section>

      <div className="h-10" />
    </SiteShell>
  );
}

function defaultServices(city: string) {
  return [
    { title: "Akut elektriker", body: `Hurtig udrykning i ${city} ved strømsvigt, kortslutning eller HPFI der slår fra.` },
    { title: "Ladestandere", body: `Installation af ladeboks til elbil i ${city}, privat og erhverv, med tilskudsmuligheder.` },
    { title: "El-tjek og fejlfinding", body: `Systematisk fejlfinding og dokumenteret el-tjek af din bolig i ${city}.` },
    { title: "Belysning & LED", body: `Lysdesign, spots, sensorer og energivenlig LED i hele ${city}.` },
    { title: "Renovering & nybyg", body: `Total el-entreprise ved renovering og tilbygning i ${city}.` },
    { title: "Smart home", body: `KNX og smart-home-løsninger installeret og konfigureret hos dig i ${city}.` },
  ];
}

function localJobBody(title: string, city: string) {
  const t = title.toLowerCase();
  if (t.includes("akut")) return `Hurtig udrykning ved strømsvigt, kortslutning og HPFI der slår fra i ${city}.`;
  if (t.includes("lade")) return `Installation af ladeboks og lade-parker til elbil i ${city}, også til ejerforeninger.`;
  if (t.includes("varme")) return `Installation og service af varmepumper i ${city}, luft-til-luft og luft-til-vand.`;
  if (t.includes("sol")) return `Solceller, batterier og smart energistyring til boliger i ${city}.`;
  if (t.includes("fejl") || t.includes("tjek")) return `Systematisk fejlfinding og dokumenteret el-tjek i ${city}.`;
  if (t.includes("lys") || t.includes("led") || t.includes("belysning")) return `Lysdesign, LED-spots og scenestyring i hjem og virksomheder i ${city}.`;
  if (t.includes("renov")) return `Total el-renovering af bolig eller erhvervslejemål i ${city}.`;
  if (t.includes("smart") || t.includes("knx")) return `KNX og smart-home installeret og konfigureret i ${city}.`;
  if (t.includes("tavle")) return `Udskiftning af eltavle og opgradering af gruppefordeling i ${city}.`;
  if (t.includes("forening") || t.includes("erhverv")) return `Serviceaftaler og løbende drift for foreninger og virksomheder i ${city}.`;
  if (t.includes("nybyg") || t.includes("tilbyg")) return `Total el-entreprise ved nybyg og tilbygning i ${city}.`;
  if (t.includes("data") || t.includes("net")) return `Netværk, data og WiFi-mesh til hjem og kontor i ${city}.`;
  return `${title} i ${city}, udført af autoriseret elektriker med garanti.`;
}

export function locationHead(city: string, slug: string) {
  const title = `Elektriker ${city} | Hurtig & autoriseret | Elektriker-Elektriker`;
  const description = `Autoriseret elektriker i ${city}. Lokal udrykning, faste priser, garanti og døgnvagt. Ring +45 28 7000 28 eller indhent et uforpligtende tilbud.`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `/${slug}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `https://elektriker-elektriker.dk/${slug}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `Elektriker-Elektriker, ${city}`,
          image: "/og-image.jpg",
          telephone: "+4528700028",
          email: "el@elektriker-elektriker.dk",
          url: `/${slug}`,
          address: {
            "@type": "PostalAddress",
            streetAddress: "Østersøgade 106A",
            postalCode: "2100",
            addressLocality: "København Ø",
            addressCountry: "DK",
          },
          areaServed: city,
          priceRange: "$$",
        }),
      },
    ],
  };
}
